// ============================================================
// PIXEL FONT — Unifont bitmap font for crisp pixel-art text
// ============================================================
//
// Phaser's Text objects render via canvas 2D context, which
// anti-aliases fonts. On a pixelArt game this looks terrible.
// This module embeds GNU Unifont hex data for ASCII 32-126,
// renders a bitmap font texture at load time, and registers
// it as a Phaser BitmapFont for use with this.add.bitmapText().
//
// Each glyph is 8 pixels wide × 16 pixels tall.
// ============================================================

const GLYPH_W = 8;
const GLYPH_H = 16;

// GNU Unifont hex data for ASCII printable characters (U+0020–U+007E).
// Format: 32 hex digits per glyph = 16 rows × 1 byte (8 pixels) per row.
// Source: GNU Unifont 17.0.03 (GPL-2.0+ with font embedding exception).
const UNIFONT_HEX: Record<number, string> = {
    0x20: '00000000000000000000000000000000',
    0x21: '00000000080808080808080008080000',
    0x22: '00002222222200000000000000000000',
    0x23: '000000001212127E24247E4848480000',
    0x24: '00000000083E4948380E09493E080000',
    0x25: '00000000314A4A340808162929460000',
    0x26: '000000001C2222141829454246390000',
    0x27: '00000808080800000000000000000000',
    0x28: '00000004080810101010101008080400',
    0x29: '00000020101008080808080810102000',
    0x2A: '00000000000008492A1C2A4908000000',
    0x2B: '0000000000000808087F080808000000',
    0x2C: '00000000000000000000000018080810',
    0x2D: '0000000000000000003C000000000000',
    0x2E: '00000000000000000000000018180000',
    0x2F: '00000000020204080810102040400000',
    0x30: '00000000182442464A52624224180000',
    0x31: '000000000818280808080808083E0000',
    0x32: '000000003C4242020C102040407E0000',
    0x33: '000000003C4242021C020242423C0000',
    0x34: '00000000040C142444447E0404040000',
    0x35: '000000007E4040407C020202423C0000',
    0x36: '000000001C2040407C424242423C0000',
    0x37: '000000007E0202040404080808080000',
    0x38: '000000003C4242423C424242423C0000',
    0x39: '000000003C4242423E02020204380000',
    0x3A: '00000000000018180000001818000000',
    0x3B: '00000000000018180000001808081000',
    0x3C: '00000000000204081020100804020000',
    0x3D: '000000000000007E0000007E00000000',
    0x3E: '00000000004020100804081020400000',
    0x3F: '000000003C4242020408080008080000',
    0x40: '000000001C224A565252524E201E0000',
    0x41: '0000000018242442427E424242420000',
    0x42: '000000007C4242427C424242427C0000',
    0x43: '000000003C42424040404042423C0000',
    0x44: '00000000784442424242424244780000',
    0x45: '000000007E4040407C404040407E0000',
    0x46: '000000007E4040407C40404040400000',
    0x47: '000000003C424240404E4242463A0000',
    0x48: '00000000424242427E42424242420000',
    0x49: '000000003E08080808080808083E0000',
    0x4A: '000000001F0404040404044444380000',
    0x4B: '00000000424448506060504844420000',
    0x4C: '000000004040404040404040407E0000',
    0x4D: '00000000424266665A5A424242420000',
    0x4E: '0000000042626252524A4A4646420000',
    0x4F: '000000003C42424242424242423C0000',
    0x50: '000000007C4242427C40404040400000',
    0x51: '000000003C4242424242425A663C0300',
    0x52: '000000007C4242427C48444442420000',
    0x53: '000000003C424240300C0242423C0000',
    0x54: '000000007F0808080808080808080000',
    0x55: '000000004242424242424242423C0000',
    0x56: '00000000414141222222141408080000',
    0x57: '00000000424242425A5A666642420000',
    0x58: '00000000424224241818242442420000',
    0x59: '00000000414122221408080808080000',
    0x5A: '000000007E02020408102040407E0000',
    0x5B: '0000000E080808080808080808080E00',
    0x5C: '00000000404020101008080402020000',
    0x5D: '00000070101010101010101010107000',
    0x5E: '00001824420000000000000000000000',
    0x5F: '00000000000000000000000000007F00',
    0x60: '00201008000000000000000000000000',
    0x61: '0000000000003C42023E4242463A0000',
    0x62: '0000004040405C6242424242625C0000',
    0x63: '0000000000003C4240404040423C0000',
    0x64: '0000000202023A4642424242463A0000',
    0x65: '0000000000003C42427E4040423C0000',
    0x66: '0000000C1010107C1010101010100000',
    0x67: '0000000000023A44444438203C42423C',
    0x68: '0000004040405C624242424242420000',
    0x69: '000000080800180808080808083E0000',
    0x6A: '0000000404000C040404040404044830',
    0x6B: '00000040404044485060504844420000',
    0x6C: '000000180808080808080808083E0000',
    0x6D: '00000000000076494949494949490000',
    0x6E: '0000000000005C624242424242420000',
    0x6F: '0000000000003C4242424242423C0000',
    0x70: '0000000000005C6242424242625C4040',
    0x71: '0000000000003A4642424242463A0202',
    0x72: '0000000000005C624240404040400000',
    0x73: '0000000000003C4240300C02423C0000',
    0x74: '000000001010107C10101010100C0000',
    0x75: '000000000000424242424242463A0000',
    0x76: '00000000000042424224242418180000',
    0x77: '00000000000041494949494949360000',
    0x78: '00000000000042422418182442420000',
    0x79: '0000000000004242424242261A02023C',
    0x7A: '0000000000007E0204081020407E0000',
    0x7B: '0000000C10100808102010080810100C',
    0x7C: '00000808080808080808080808080808',
    0x7D: '00000030080810100804081010080830',
    0x7E: '00000031494600000000000000000000',
};

const FONT_KEY = 'unifont';
const FIRST_CHAR = 0x20;
const LAST_CHAR = 0x7E;
const CHAR_COUNT = LAST_CHAR - FIRST_CHAR + 1; // 95

// Arrange glyphs in a grid on the texture atlas
const ATLAS_COLS = 16;

/**
 * Generates a Phaser BitmapFont from the embedded Unifont hex data.
 * Call once before any scene uses bitmapText. Subsequent calls are no-ops.
 */
export function createPixelFont(scene: Phaser.Scene): void {
    // Already registered
    if (scene.cache.bitmapFont.has(FONT_KEY)) return;

    const atlasRows = Math.ceil(CHAR_COUNT / ATLAS_COLS);
    const texW = ATLAS_COLS * GLYPH_W;
    const texH = atlasRows * GLYPH_H;

    // Create a canvas and render every glyph from hex data
    const canvas = document.createElement('canvas');
    canvas.width = texW;
    canvas.height = texH;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, texW, texH);

    for (let code = FIRST_CHAR; code <= LAST_CHAR; code++) {
        const hex = UNIFONT_HEX[code];
        if (!hex) continue;

        const index = code - FIRST_CHAR;
        const col = index % ATLAS_COLS;
        const row = Math.floor(index / ATLAS_COLS);
        const baseX = col * GLYPH_W;
        const baseY = row * GLYPH_H;

        // Each pair of hex digits = one row of 8 pixels
        for (let r = 0; r < GLYPH_H; r++) {
            const byteHex = hex.substring(r * 2, r * 2 + 2);
            const rowBits = parseInt(byteHex, 16);
            for (let c = 0; c < GLYPH_W; c++) {
                if (rowBits & (0x80 >> c)) {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(baseX + c, baseY + r, 1, 1);
                }
            }
        }
    }

    // Add the canvas as a Phaser texture
    scene.textures.addCanvas(FONT_KEY, canvas);

    // Build BitmapFont data manually (RetroFont.Parse has issues with
    // canvas textures). We construct the exact structure Phaser expects.
    const frame = scene.textures.getFrame(FONT_KEY);
    const srcW = frame.source.width;
    const srcH = frame.source.height;

    const chars: Record<number, any> = {};
    for (let code = FIRST_CHAR; code <= LAST_CHAR; code++) {
        const index = code - FIRST_CHAR;
        const col = index % ATLAS_COLS;
        const row = Math.floor(index / ATLAS_COLS);
        const x = col * GLYPH_W;
        const y = row * GLYPH_H;

        chars[code] = {
            x: x,
            y: y,
            width: GLYPH_W,
            height: GLYPH_H,
            centerX: Math.floor(GLYPH_W / 2),
            centerY: Math.floor(GLYPH_H / 2),
            xOffset: 0,
            yOffset: 0,
            xAdvance: GLYPH_W,
            data: {},
            kerning: {},
            u0: x / srcW,
            v0: y / srcH,
            u1: (x + GLYPH_W) / srcW,
            v1: (y + GLYPH_H) / srcH,
        };
    }

    const fontData = {
        retroFont: true,
        font: FONT_KEY,
        size: GLYPH_W,
        lineHeight: GLYPH_H + 2,
        chars: chars,
    };

    scene.cache.bitmapFont.add(FONT_KEY, {
        data: fontData,
        texture: FONT_KEY,
        frame: null,
    });
}

/** Key to use with this.add.bitmapText() */
export const PIXEL_FONT_KEY = FONT_KEY;
export const PIXEL_FONT_SIZE = GLYPH_H;

// ============================================================
// MICRO FONT — 4×6 bitmap font for compact UI text
// ============================================================
// Hand-designed at 4 pixels wide × 6 pixels tall per glyph.
// Each row is 4 bits encoded as one hex digit (MSB = leftmost pixel).
// 6 rows per glyph → 6 hex digits per entry.
// Lowercase maps to uppercase at this size for clarity.

const MICRO_W = 4;
const MICRO_H = 6;

const MICRO_HEX: Record<number, string> = {
    0x20: '000000', //
    0x21: '444040', // !
    0x22: 'AA0000', // "
    0x23: '5F5F50', // #
    0x24: '7A65E0', // $
    0x25: '924900', // %
    0x26: '4A4A50', // &
    0x27: '440000', // '
    0x28: '244420', // (
    0x29: '422240', // )
    0x2A: 'A4A000', // *
    0x2B: '04E400', // +
    0x2C: '000048', // ,
    0x2D: '00E000', // -
    0x2E: '000040', // .
    0x2F: '124800', // /
    0x30: '699960', // 0
    0x31: '4C44E0', // 1
    0x32: '6168F0', // 2
    0x33: 'E161E0', // 3
    0x34: 'AAF220', // 4
    0x35: 'F8E1E0', // 5
    0x36: '68E960', // 6
    0x37: 'F12440', // 7
    0x38: '696960', // 8
    0x39: '697160', // 9
    0x3A: '040400', // :
    0x3B: '040480', // ;
    0x3C: '248420', // <
    0x3D: '0F0F00', // =
    0x3E: '842480', // >
    0x3F: '692020', // ?
    0x40: '6BB870', // @
    0x41: '69F990', // A
    0x42: 'E9E9E0', // B
    0x43: '688860', // C
    0x44: 'E999E0', // D
    0x45: 'F8E8F0', // E
    0x46: 'F8E880', // F
    0x47: '68B970', // G
    0x48: '99F990', // H
    0x49: 'E444E0', // I
    0x4A: '311960', // J
    0x4B: '9ACA90', // K
    0x4C: '8888F0', // L
    0x4D: '9FF990', // M
    0x4E: '9DB990', // N
    0x4F: '699960', // O
    0x50: 'E9E880', // P
    0x51: '699A50', // Q
    0x52: 'E9EA90', // R
    0x53: '7861E0', // S
    0x54: 'F44440', // T
    0x55: '999960', // U
    0x56: '999660', // V
    0x57: '99AF90', // W
    0x58: '996990', // X
    0x59: '996440', // Y
    0x5A: 'F168F0', // Z
    0x5B: '644460', // [
    0x5C: '842100', // backslash
    0x5D: '622260', // ]
    0x5E: '690000', // ^
    0x5F: '00000F', // _
    0x60: '420000', // `
    0x61: '69F990', // a
    0x62: 'E9E9E0', // b
    0x63: '688860', // c
    0x64: 'E999E0', // d
    0x65: 'F8E8F0', // e
    0x66: 'F8E880', // f
    0x67: '68B970', // g
    0x68: '99F990', // h
    0x69: 'E444E0', // i
    0x6A: '311960', // j
    0x6B: '9ACA90', // k
    0x6C: '8888F0', // l
    0x6D: '9FF990', // m
    0x6E: '9DB990', // n
    0x6F: '699960', // o
    0x70: 'E9E880', // p
    0x71: '699A50', // q
    0x72: 'E9EA90', // r
    0x73: '7861E0', // s
    0x74: 'F44440', // t
    0x75: '999960', // u
    0x76: '999660', // v
    0x77: '99AF90', // w
    0x78: '996990', // x
    0x79: '996440', // y
    0x7A: 'F168F0', // z
    0x7B: '324230', // {
    0x7C: '444440', // |
    0x7D: 'C424C0', // }
    0x7E: '05A000', // ~
};

const MICRO_KEY = 'microfont';
const MICRO_ATLAS_COLS = 16;

/**
 * Generates a 4×6 micro BitmapFont for compact text.
 * Call once before any scene uses MICRO_FONT_KEY. Subsequent calls are no-ops.
 */
export function createMicroFont(scene: Phaser.Scene): void {
    if (scene.cache.bitmapFont.has(MICRO_KEY)) return;

    const charCount = LAST_CHAR - FIRST_CHAR + 1;
    const atlasRows = Math.ceil(charCount / MICRO_ATLAS_COLS);
    const texW = MICRO_ATLAS_COLS * MICRO_W;
    const texH = atlasRows * MICRO_H;

    const canvas = document.createElement('canvas');
    canvas.width = texW;
    canvas.height = texH;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, texW, texH);

    for (let code = FIRST_CHAR; code <= LAST_CHAR; code++) {
        const hex = MICRO_HEX[code];
        if (!hex) continue;

        const index = code - FIRST_CHAR;
        const col = index % MICRO_ATLAS_COLS;
        const row = Math.floor(index / MICRO_ATLAS_COLS);
        const baseX = col * MICRO_W;
        const baseY = row * MICRO_H;

        for (let r = 0; r < MICRO_H; r++) {
            const rowBits = parseInt(hex[r], 16);
            for (let c = 0; c < MICRO_W; c++) {
                if (rowBits & (0x8 >> c)) {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(baseX + c, baseY + r, 1, 1);
                }
            }
        }
    }

    scene.textures.addCanvas(MICRO_KEY, canvas);

    const frame = scene.textures.getFrame(MICRO_KEY);
    const srcW = frame.source.width;
    const srcH = frame.source.height;

    const chars: Record<number, any> = {};
    for (let code = FIRST_CHAR; code <= LAST_CHAR; code++) {
        const index = code - FIRST_CHAR;
        const col = index % MICRO_ATLAS_COLS;
        const row = Math.floor(index / MICRO_ATLAS_COLS);
        const x = col * MICRO_W;
        const y = row * MICRO_H;

        chars[code] = {
            x, y,
            width: MICRO_W,
            height: MICRO_H,
            centerX: Math.floor(MICRO_W / 2),
            centerY: Math.floor(MICRO_H / 2),
            xOffset: 0,
            yOffset: 0,
            xAdvance: MICRO_W + 1, // 1px inter-character gap
            data: {},
            kerning: {},
            u0: x / srcW,
            v0: y / srcH,
            u1: (x + MICRO_W) / srcW,
            v1: (y + MICRO_H) / srcH,
        };
    }

    const fontData = {
        retroFont: true,
        font: MICRO_KEY,
        size: MICRO_W,
        lineHeight: MICRO_H + 1,
        chars,
    };

    scene.cache.bitmapFont.add(MICRO_KEY, {
        data: fontData,
        texture: MICRO_KEY,
        frame: null,
    });
}

export const MICRO_FONT_KEY = MICRO_KEY;
export const MICRO_FONT_SIZE = MICRO_H;
